import { supabase } from './supabase';
import { OpenAPI } from '../app/api/core/OpenAPI';

/** Max time to wait for Supabase to restore session from storage (e.g. after refresh). */
export const AUTH_SESSION_READY_TIMEOUT_MS = 2000;

/**
 * Wait for Supabase to finish initializing auth state (e.g. after full page reload).
 * Uses onAuthStateChange so we don't rely on fixed delays. Resolves with the initial
 * session (or null) when the client has rehydrated, or after timeout.
 *
 * @param {Object} [options]
 * @param {number} [options.timeoutMs] - Max wait; defaults to AUTH_SESSION_READY_TIMEOUT_MS
 * @returns {Promise<{ session: object | null }>}
 */
export function waitForSessionReady(options = {}) {
    const timeoutMs = options.timeoutMs ?? AUTH_SESSION_READY_TIMEOUT_MS;

    return new Promise((resolve) => {
        let settled = false;

        const settle = (session) => {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            subscription.unsubscribe();
            resolve({ session });
        };

        const timeoutId = setTimeout(() => {
            if (settled) return;
            settled = true;
            subscription.unsubscribe();
            supabase.auth.getSession().then(({ data: { session } }) => {
                resolve({ session });
            });
        }, timeoutMs);

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            settle(session ?? null);
        });
    });
}

/**
 * Get the current Supabase session token.
 * Always attempts to refresh the session so we never return an expired access token
 * (getSession() can return a cached session with an expired token, causing API 401s
 * while the UI still shows logged in).
 */
export async function getAuthToken() {
    try {
        const { data: { session: existingSession } } = await supabase.auth.getSession();

        // Always try to refresh when we might have a session, so we don't return an expired token.
        // refreshSession() uses the refresh token to get a new access token from Supabase.
        const { data: { session: refreshedSession } } = await supabase.auth.refreshSession();
        const session = refreshedSession || existingSession;

        return session?.access_token || null;
    } catch (error) {
        console.error('Error getting auth token:', error);
        return null;
    }
}

/**
 * Set the auth token in OpenAPI config
 * Returns a function to restore the original token
 */
export async function setAuthToken() {
    const token = await getAuthToken();
    if (!token) {
        throw new Error('Not authenticated. Please log in again.');
    }

    const originalToken = OpenAPI.TOKEN;
    OpenAPI.TOKEN = token;

    // Return a cleanup function to restore the original token
    return () => {
        OpenAPI.TOKEN = originalToken;
    };
}

/**
 * Execute an API call with authentication
 * Automatically sets and restores the auth token
 */
export async function withAuth(apiCall) {
    const cleanup = await setAuthToken();
    try {
        return await apiCall();
    } finally {
        cleanup();
    }
}

/**
 * Get the current Supabase user
 * @returns {Promise<Object|null>} Supabase user object or null
 */
export async function getCurrentUser() {
    try {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error) {
            console.error('Error getting user:', error);
            return null;
        }
        return user;
    } catch (error) {
        console.error('Error getting user:', error);
        return null;
    }
}

/**
 * Refresh the current session to get updated permissions
 * Permissions are synced to JWT token, so refreshing will get the latest permissions
 * @returns {Promise<Object|null>} Updated session or null
 */
export async function refreshSession() {
    try {
        const { data: { session }, error } = await supabase.auth.refreshSession();
        if (error) {
            console.error('Error refreshing session:', error);
            return null;
        }
        return session;
    } catch (error) {
        console.error('Error refreshing session:', error);
        return null;
    }
}

/**
 * Logout the current user
 * Signs out from Supabase and clears local storage
 */
export async function logout() {
    try {
        // Sign out from Supabase
        await supabase.auth.signOut();

        // Clear local storage
        localStorage.clear();

        // Clear OpenAPI token
        OpenAPI.TOKEN = null;

        return true;
    } catch (error) {
        console.error('Error logging out:', error);
        return false;
    }
}

